// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditEventListItem } from '@/audit/components/audit-event-list-item';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { getItem } from '@/items/api/get-item';
import { getItemAuditEvents } from '@/items/api/get-item-audit-events';
import { Stack, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Navigate, createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/catalog/items/$itemKey/audit')({
  component: ItemAuditRoute,
});

/**
 * Item audit page component.
 *
 * Features:
 * - Displays audit history for SERVICE and DEVICE items
 * - BULK items redirect to the inventory page (no audit support yet)
 * - Shows a message when there are no audit events
 *
 * TODO(#15955): Add audit support for BULK/parts items when implemented
 */
function ItemAuditRoute() {
  const { itemKey } = Route.useParams();

  const { data: itemData } = useSuspenseQuery({
    queryKey: ['item', itemKey],
    queryFn: () => getItem(itemKey),
  });

  // BULK items (isPhysical && !hasAssets) don't have audit events yet
  if (itemData.item.isPhysical && !itemData.item.hasAssets) {
    return (
      <Navigate
        to="/catalog/items/$itemKey/units"
        params={{ itemKey }}
        replace
      />
    );
  }

  return <ItemAuditContent itemKey={itemKey} />;
}

function ItemAuditContent(props: { itemKey: string }) {
  const { data: auditEvents } = useSuspenseQuery({
    queryKey: ['item', props.itemKey, 'audit'],
    queryFn: () => getItemAuditEvents(props.itemKey),
  });

  return (
    <HardfinTabCardWrapper title="Audit log">
      <Stack spacing={2} paddingTop={2}>
        {auditEvents.length === 0 ? (
          <Typography color="text.secondary" textAlign="center">
            No audit events for this item
          </Typography>
        ) : (
          auditEvents.map((event) => (
            <AuditEventListItem key={event.id} event={event} />
          ))
        )}
      </Stack>
    </HardfinTabCardWrapper>
  );
}
